// Robust guard around `customElements.define` so a misbehaving widget
// bundle can never crash the viewer.
//
// Stdlib widget bundles (src/generated/stdlib-widgets/*) and, in
// server mode, runtime-loaded user-package bundles all call
// `customElements.define()` as an unguarded import side effect. The
// second `define()` of a name throws
//
//   NotSupportedError: Failed to execute 'define' on
//   'CustomElementRegistry': the name "arch-…" has already been used
//
// and — because the bundle is a side-effect import — that throw aborts
// the entire module graph, leaving a blank viewer. A duplicated or
// wrongly-imported package that re-declares an existing `arch-*` tag is
// enough to trigger it.
//
// Patching the registry once, before any bundle runs, contains the
// blast radius to the single offending tag: the first definition wins
// and every later duplicate / throwing define degrades to a recorded
// warning instead of a crash. Warnings are exposed so the app can
// surface them as model warnings in the diagnostics panel.

export interface WidgetDefineWarning {
  readonly tag: string;
  readonly reason: "duplicate" | "error";
  readonly message: string;
}

const warnings: WidgetDefineWarning[] = [];
const listeners = new Set<(w: readonly WidgetDefineWarning[]) => void>();
let installed = false;

/** Snapshot of every widget-registration warning recorded so far. */
export function widgetDefineWarnings(): readonly WidgetDefineWarning[] {
  return warnings;
}

/**
 * Subscribe to widget-registration warnings. Fires immediately if any
 * warnings were already recorded (bundles run at import time, before
 * React mounts). Returns an unsubscribe fn.
 */
export function onWidgetDefineWarning(
  fn: (w: readonly WidgetDefineWarning[]) => void
): () => void {
  listeners.add(fn);
  if (warnings.length > 0) fn(warnings);
  return () => {
    listeners.delete(fn);
  };
}

function record(w: WidgetDefineWarning): void {
  warnings.push(w);
  console.warn(`[archlang] widget '${w.tag}' skipped: ${w.message}`);
  for (const fn of listeners) fn(warnings);
}

/**
 * Wrap `customElements.define` so duplicate names and thrown errors
 * become warnings instead of crashes. Idempotent and a no-op when
 * there is no custom-element registry (SSR).
 */
export function installWidgetDefineGuard(): void {
  if (installed) return;
  if (typeof customElements === "undefined") return;
  installed = true;

  const registry = customElements;
  const original = registry.define.bind(registry);

  registry.define = function guardedDefine(
    name: string,
    ctor: CustomElementConstructor,
    options?: ElementDefinitionOptions
  ): void {
    // Already registered → a duplicate or conflicting package is
    // re-declaring a tag. First definition wins; skip and warn.
    if (registry.get(name)) {
      record({
        tag: name,
        reason: "duplicate",
        message: `'${name}' already registered — ignoring duplicate definition (duplicate or conflicting package?)`,
      });
      return;
    }
    try {
      original(name, ctor, options);
    } catch (err) {
      // Invalid name, constructor reused under another tag, etc. Never
      // let it escape and abort the bundle's module graph.
      record({
        tag: name,
        reason: "error",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  } as typeof registry.define;
}

// Self-install on import so simply importing this module before any
// widget bundle is enough to arm the guard.
installWidgetDefineGuard();
